/**
 * WelcomeMoment - the one-time "Welcome to Olive!" greeting on Home
 * (LP-49).
 *
 * Shows when Home has loaded whoami and tenant config, the tenant's
 * `features.welcome_day` (welcomeDay) is today in the browser's own time
 * zone, and this browser has not shown it before. The once-key
 * `olive.welcome.<tenantSlug>.<day>` is written before anything renders;
 * a browser whose localStorage throws sees nothing, since it could not
 * remember and would greet on every visit. Once-ness is per browser.
 *
 * The layer covers the viewport for about four seconds: the greeting in
 * the centre, confetti in the brand tokens on a canvas, and Olive's dove
 * flying one arc over the greeting while a rainbow draws in behind her
 * (welcomeArc below; the motion is in src/index.css), then a fade.
 * Any click or key ends it at once. Under prefers-reduced-motion the
 * greeting stays for the same time with the dove still beside it, no
 * canvas and no fade. Focus never moves.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

const SHOW_MS = 4000;
const FADE_MS = 360;
const PIECE_COUNT = 160;
/** Olive's own mark, the dove with the martini (SignInScreen, AppShell). */
const DOVE_SRC = '/brand/olive-dove-martini.png';
/** The rainbow's bands, outer to inner, each a brand token. */
const RAINBOW_BANDS = [
  'stroke-terracotta-soft',
  'stroke-warm-400',
  'stroke-warm-300',
  'stroke-sage-400',
  'stroke-heather-deep',
] as const;
const RAINBOW_OPACITY = 0.55;
/** Points sampled along each band's path. */
const ARC_SAMPLES = 120;

/**
 * The one arc, a cubic curve in percent of the layer on each axis: from
 * past the lower left, over the greeting with its peak near (50, 15), to
 * past the lower right. Steep enough at the sides that it clears a
 * phone-width greeting, and its ends far enough out that the innermost
 * band's feet start off a phone's screen.
 */
type Point = readonly [number, number];
const ARC: readonly [Point, Point, Point, Point] = [
  [-14, 78],
  [-6, -6],
  [106, -6],
  [114, 78],
];

export interface WelcomeArc {
  /** The layer's size, the rainbow's viewBox. */
  width: number;
  height: number;
  /** The arc, for the dove's offset-path. */
  dove: string;
  /** Each band's path, outer to inner. */
  bands: string[];
  /** Each band's stroke width. */
  bandWidth: number;
}

/**
 * The arc on a layer of `w` by `h` pixels, everything in pixels. The
 * rainbow is drawn in pixels rather than in a stretched 0 to 100 viewBox:
 * a stretched box needs non-scaling-stroke to keep the bands even, and
 * under it Chrome measures the dash in screen units, not in pathLength,
 * so the bands drew on in pieces. The outer band rides the arc itself,
 * where the dove flies; each inner band is the arc moved one band width
 * toward its inside, so the bands stay concentric.
 */
export function welcomeArc(w: number, h: number): WelcomeArc {
  const px = ([x, y]: Point): Point => [(x * w) / 100, (y * h) / 100];
  const [p0, p1, p2, p3] = [px(ARC[0]), px(ARC[1]), px(ARC[2]), px(ARC[3])];
  const point = (t: number, i: 0 | 1): number => {
    const u = 1 - t;
    return u * u * u * p0[i] + 3 * u * u * t * p1[i] + 3 * u * t * t * p2[i] + t * t * t * p3[i];
  };
  const slope = (t: number, i: 0 | 1): number => {
    const u = 1 - t;
    return 3 * u * u * (p1[i] - p0[i]) + 6 * u * t * (p2[i] - p1[i]) + 3 * t * t * (p3[i] - p2[i]);
  };
  const bandWidth = w >= 768 ? 16 : 10;
  const f = (n: number): string => n.toFixed(2);
  const bands = RAINBOW_BANDS.map((_, k) => {
    const pts: string[] = [];
    for (let i = 0; i <= ARC_SAMPLES; i += 1) {
      const t = i / ARC_SAMPLES;
      const dx = slope(t, 0);
      const dy = slope(t, 1);
      const len = Math.hypot(dx, dy) || 1;
      // The inside of the arc: below it at the peak.
      const x = point(t, 0) + (-dy / len) * k * bandWidth;
      const y = point(t, 1) + (dx / len) * k * bandWidth;
      pts.push(`${f(x)} ${f(y)}`);
    }
    return `M ${pts.join(' L ')}`;
  });
  const dove = `M ${f(p0[0])} ${f(p0[1])} C ${f(p1[0])} ${f(p1[1])} ${f(p2[0])} ${f(p2[1])} ${f(p3[0])} ${f(p3[1])}`;
  return { width: w, height: h, dove, bands, bandWidth };
}

/** The brand tokens the confetti is drawn in, read from the stylesheet. */
const CONFETTI_TOKENS = [
  '--color-sage-400',
  '--color-sage-800',
  '--color-sage-200',
  '--color-warm-300',
  '--color-warm-400',
  '--color-heather-deep',
  '--color-terracotta-soft',
] as const;

type Phase = 'idle' | 'showing' | 'fading' | 'done';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Today in the browser's local time zone, as YYYY-MM-DD. */
function localTodayIso(now: Date): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** True once the once-key is written; false when already shown or when
 *  localStorage cannot be read or written. */
function claimOnce(key: string): boolean {
  try {
    if (window.localStorage.getItem(key) !== null) return false;
    window.localStorage.setItem(key, 'shown');
    return true;
  } catch {
    return false;
  }
}

function brandColors(): string[] {
  const style = getComputedStyle(document.documentElement);
  return CONFETTI_TOKENS.map((t) => style.getPropertyValue(t).trim()).filter((c) => c !== '');
}

/** Draws about four seconds of falling confetti; returns a stop function. */
function runConfetti(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d');
  const colors = brandColors();
  if (!ctx || colors.length === 0) return () => {};
  const dpr = window.devicePixelRatio || 1;
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  ctx.scale(dpr, dpr);

  const pieces = Array.from({ length: PIECE_COUNT }, () => ({
    x: Math.random() * w,
    y: -20 - Math.random() * h * 0.4,
    // Each piece crosses the screen in 2.2 to 3.4 seconds.
    speed: (h + h * 0.4 + 40) / (2200 + Math.random() * 1200),
    sway: 12 + Math.random() * 28,
    swayRate: 0.002 + Math.random() * 0.003,
    phase: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.02,
    pw: 6 + Math.random() * 6,
    ph: 8 + Math.random() * 8,
    color: colors[Math.floor(Math.random() * colors.length)] as string,
  }));

  let raf = 0;
  const start = performance.now();
  const frame = (now: number): void => {
    const t = now - start;
    ctx.clearRect(0, 0, w, h);
    for (const p of pieces) {
      const y = p.y + p.speed * t;
      if (y > h + 20) continue;
      const x = p.x + Math.sin(t * p.swayRate + p.phase) * p.sway;
      const turn = p.phase + p.spin * t;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(turn);
      ctx.scale(1, Math.cos(turn * 1.7));
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.pw / 2, -p.ph / 2, p.pw, p.ph);
      ctx.restore();
    }
    if (t < SHOW_MS) raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return () => cancelAnimationFrame(raf);
}

export interface WelcomeMomentProps {
  tenantSlug: string;
  /** welcomeDay(tenantConfig): the tenant's welcome day, or null. */
  day: string | null;
  /** Home has loaded both whoami and tenant config. */
  ready: boolean;
}

export function WelcomeMoment({ tenantSlug, day, ready }: WelcomeMomentProps): ReactNode {
  const [phase, setPhase] = useState<Phase>('idle');
  const [reduced] = useState(prefersReducedMotion);
  const checked = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Decide once, after everything Home needs has loaded.
  useEffect(() => {
    if (!ready || checked.current) return;
    checked.current = true;
    if (day === null || day !== localTodayIso(new Date())) return;
    if (!claimOnce(`olive.welcome.${tenantSlug}.${day}`)) return;
    setPhase('showing');
  }, [ready, day, tenantSlug]);

  // The clock, and any click or key ending it at once.
  useEffect(() => {
    if (phase !== 'showing') return;
    const end = (): void => setPhase('done');
    const timer = window.setTimeout(() => setPhase(reduced ? 'done' : 'fading'), SHOW_MS);
    window.addEventListener('click', end, true);
    window.addEventListener('keydown', end, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('click', end, true);
      window.removeEventListener('keydown', end, true);
    };
  }, [phase, reduced]);

  useEffect(() => {
    if (phase !== 'fading') return;
    const end = (): void => setPhase('done');
    const timer = window.setTimeout(end, FADE_MS);
    window.addEventListener('click', end, true);
    window.addEventListener('keydown', end, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('click', end, true);
      window.removeEventListener('keydown', end, true);
    };
  }, [phase]);

  const showing = phase === 'showing' || phase === 'fading';
  // Measured once, when the layer appears. The layer is the viewport
  // less any scrollbar; jsdom has no layout, so it reads the window.
  const arc = useMemo(() => {
    if (!showing || reduced) return null;
    const root = document.documentElement;
    return welcomeArc(root.clientWidth || window.innerWidth, root.clientHeight || window.innerHeight);
  }, [showing, reduced]);
  useEffect(() => {
    if (!showing || reduced || !canvasRef.current) return;
    return runConfetti(canvasRef.current);
  }, [showing, reduced]);

  if (!showing) return null;
  return (
    <div
      data-testid="hub-welcome"
      className={`fixed inset-0 z-[60] flex items-center overflow-hidden justify-center bg-cream/80 transition-opacity duration-[var(--dur-slow)] ease-quiet ${
        phase === 'fading' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {!reduced && (
        <canvas
          ref={canvasRef}
          data-testid="hub-welcome-confetti"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
      )}
      {arc !== null && (
        <svg
          aria-hidden="true"
          data-testid="hub-welcome-rainbow"
          viewBox={`0 0 ${arc.width} ${arc.height}`}
          className="animate-rainbow-leave pointer-events-none absolute inset-0 h-full w-full"
        >
          {arc.bands.map((d, k) => (
            <path
              key={RAINBOW_BANDS[k]}
              d={d}
              pathLength={1}
              fill="none"
              strokeLinecap="round"
              strokeWidth={arc.bandWidth}
              strokeOpacity={RAINBOW_OPACITY}
              className={`animate-rainbow-draw ${RAINBOW_BANDS[k]}`}
            />
          ))}
        </svg>
      )}
      {arc !== null && (
        <div
          aria-hidden="true"
          data-testid="hub-welcome-dove"
          className="animate-dove-flight pointer-events-none absolute left-0 top-0"
          style={{ offsetPath: `path("${arc.dove}")` }}
        >
          <img src={DOVE_SRC} alt="" aria-hidden="true" className="block h-24 w-auto md:h-40" />
        </div>
      )}
      <div
        aria-live="polite"
        className="relative flex flex-col items-center gap-3 px-5 text-center md:flex-row md:gap-6"
      >
        {reduced && (
          <img
            src={DOVE_SRC}
            alt=""
            aria-hidden="true"
            data-testid="hub-welcome-dove"
            className="dove-still pointer-events-none h-24 w-auto md:h-40"
          />
        )}
        <p className="font-display text-[2rem] font-semibold leading-[1.04] tracking-[-0.01em] text-sage-800 md:text-[3.5rem]">
          Welcome to Olive!
        </p>
      </div>
    </div>
  );
}
